import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, X, Share, Plus, Mail, Check } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { api } from '../../services/api'
import { trackEvent } from '../../utils/analytics'
import { CANONICAL_ORIGIN, isLegacyOrigin } from '../../utils/canonicalOrigin'
import { isStandalone } from '../../utils/standalone'

/**
 * Phase 3 of the btvision.app migration: the only thing that moves users who
 * already have BT Vision installed.
 *
 * A PWA install is welded to its origin — there is no API on any platform that
 * migrates one. So this banner does not move anybody; it asks them to redo the
 * install by hand, and tries to make that as cheap as it can be.
 *
 * Renders on the old origin only. Two audiences, very different asks:
 *
 *   - browser tab  → one tap on a link, essentially free
 *   - installed app → open a browser (which iOS will not let us do for them),
 *                     sign in again, add to home screen again
 *
 * The email helper exists for the second case. An installed iOS PWA cannot
 * hand a cross-origin URL to Safari — the link opens in an in-app sheet where
 * "Add to Home Screen" is not available — so the escape hatch is to put the
 * link somewhere outside the app entirely, i.e. their inbox.
 */

const DISMISS_KEY = 'bt_migration_dismissed_at'
const DISMISS_DAYS = 3

/**
 * The escalation lever from §4 Phase 3. Before this date the banner can be
 * snoozed; after it, the close button is gone. Push it out if PostHog shows
 * the old origin still carrying real traffic — moving it earlier only makes
 * sense once the remaining stragglers are few enough to annoy.
 */
const STICKY_FROM = new Date('2026-11-01T00:00:00Z')

function isSnoozed(): boolean {
  if (Date.now() >= STICKY_FROM.getTime()) return false
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const days = (Date.now() - parseInt(raw, 10)) / 86_400_000
    return days < DISMISS_DAYS
  } catch {
    return false
  }
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)
}

export function MigrationBanner() {
  // Hostname is fixed for the life of the tab, so these never need to re-read.
  const [legacy] = useState(isLegacyOrigin)
  const [installed] = useState(isStandalone)
  const [snoozed, setSnoozed] = useState(isSnoozed)
  const [showGuide, setShowGuide] = useState(false)
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const { isAuthenticated } = useAuth()

  if (!legacy || snoozed) return null

  const canSnooze = Date.now() < STICKY_FROM.getTime()

  const dismiss = () => {
    trackEvent('btv_migration_banner_dismiss', { installed })
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      /* private mode — the banner simply returns next load */
    }
    setSnoozed(true)
  }

  // Straight to /login, not "/": this person already has an account, and the
  // landing page would sell them a product they are mid-way through using.
  // ?moved=1 tells that page why they are signed out, so it can explain rather
  // than present a bare form.
  const target = `${CANONICAL_ORIGIN}/login?moved=1`

  const handleOpen = () => {
    trackEvent('btv_migration_open_new_origin', { installed })
    window.open(target, '_blank', 'noopener')
  }

  const handleEmail = async () => {
    setEmailState('sending')
    trackEvent('btv_migration_email_request')
    const res = await api.vision.sendMigrationLink()
    setEmailState(res.success ? 'sent' : 'error')
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md"
      >
        <div className="glass-card rounded-2xl p-4 border border-turquoise/30 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-turquoise/20 flex items-center justify-center shrink-0">
              <ArrowRight className="w-5 h-5 text-turquoise" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white text-sm">
                O BT Vision mudou de endereço
              </p>
              <p className="text-xs text-white/50 mt-0.5">
                {installed
                  ? 'Este atalho vai parar de funcionar. Reinstale a partir de btvision.app — seus rankings continuam lá.'
                  : 'Agora estamos em btvision.app. Seus rankings continuam os mesmos.'}
              </p>
            </div>
            {canSnooze && (
              <button
                onClick={dismiss}
                aria-label="Dispensar"
                className="p-1.5 text-white/40 hover:text-white/60 hover:bg-white/10 rounded-lg transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <button
            onClick={installed ? () => { trackEvent('btv_migration_guide_view'); setShowGuide(true) } : handleOpen}
            className="mt-3 w-full py-2.5 rounded-xl bg-turquoise hover:bg-turquoise/80 text-white text-sm font-medium transition-colors"
          >
            {installed ? 'Como migrar' : 'Ir para btvision.app'}
          </button>
        </div>
      </motion.div>

      <AnimatePresence>
        {showGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/90 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowGuide(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm glass-card rounded-2xl p-6 my-auto"
            >
              <div className="flex justify-between items-start mb-1">
                <h3 className="font-display text-lg font-bold text-white">
                  Migrar para btvision.app
                </h3>
                <button
                  onClick={() => setShowGuide(false)}
                  aria-label="Fechar"
                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-white/50 text-xs mb-5">
                Nada se perde — rankings, rodadas e histórico continuam na sua conta.
                É o atalho na tela inicial que precisa ser refeito.
              </p>

              <div className="space-y-4">
                <Step n={1} title="Abra btvision.app no navegador">
                  {isAuthenticated
                    ? 'Use o botão abaixo para receber o link por email — assim ele abre no Safari de verdade, e não dentro do app.'
                    : 'Digite btvision.app na barra de endereço do navegador.'}
                </Step>
                <Step n={2} title="Entre com seu email">
                  Você recebe um link mágico, como da primeira vez.
                </Step>
                {isIOS() ? (
                  <>
                    <Step n={3} title="Toque em Compartilhar" icon={<Share className="w-3.5 h-3.5 inline" />}>
                      O ícone na barra do navegador.
                    </Step>
                    <Step n={4} title="Adicionar à Tela de Início" icon={<Plus className="w-3.5 h-3.5 inline" />}>
                      E confirme em "Adicionar".
                    </Step>
                    <Step n={5} title="Apague o atalho antigo">
                      Segure o ícone antigo e escolha "Remover".
                    </Step>
                  </>
                ) : (
                  <>
                    <Step n={3} title="Instale pelo menu do navegador">
                      Toque em "Instalar app" ou "Adicionar à tela inicial".
                    </Step>
                    <Step n={4} title="Apague o atalho antigo">
                      Segure o ícone antigo e escolha "Desinstalar".
                    </Step>
                  </>
                )}
              </div>

              {isAuthenticated && (
                <button
                  onClick={handleEmail}
                  disabled={emailState === 'sending' || emailState === 'sent'}
                  className="mt-5 w-full py-2.5 rounded-xl bg-turquoise hover:bg-turquoise/80 disabled:bg-turquoise/40 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {emailState === 'sent' ? (
                    <><Check className="w-4 h-4" /> Link enviado — confira seu email</>
                  ) : (
                    <><Mail className="w-4 h-4" /> {emailState === 'sending' ? 'Enviando...' : 'Receber o link por email'}</>
                  )}
                </button>
              )}
              {emailState === 'error' && (
                <p className="mt-2 text-xs text-coral text-center">
                  Não deu para enviar. Abra btvision.app no navegador manualmente.
                </p>
              )}

              <button
                onClick={handleOpen}
                className="mt-2 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white/70 text-sm transition-colors"
              >
                Abrir btvision.app agora
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function Step({
  n,
  title,
  icon,
  children,
}: {
  n: number
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-sm font-bold text-turquoise">
        {n}
      </div>
      <div>
        <p className="text-white text-sm font-medium flex items-center gap-1">
          {title} {icon}
        </p>
        <p className="text-white/50 text-xs mt-0.5">{children}</p>
      </div>
    </div>
  )
}
